import { page } from '$app/stores';
import { type Cart } from '$lib/types/ecom/apiTypes';
import { gql, GraphQLClient } from 'graphql-request';
import { get } from 'svelte/store';

let graphQLClient: GraphQLClient;

export function init(graphQLClientProp: GraphQLClient) {
  graphQLClient = graphQLClientProp;
}

function getContext() {
  const lang = (get(page).params.lang ?? 'en').toUpperCase();
  const country = get(page).data.location;

  return `@inContext(language: ${lang})`;
}

const cartQuery = gql`
  id
  createdAt
  updatedAt

  buyerIdentity {
    customer {
      id
    }
  }

  lines(first: 20) {
    edges {
      node {
        id
        quantity
        cost {
          amountPerQuantity {
            amount
            currencyCode
          }
          compareAtAmountPerQuantity {
            amount
            currencyCode
          }
          totalAmount {
            amount
            currencyCode
          }
        }
        merchandise {
          ... on ProductVariant {
            id
            product {
              handle
              title
              product_type: metafield(namespace: "custom", key: "product_type") {
                value
              }
            }
            selectedOptions {
              name
              value
            }
            image {
              id
              altText
              url (transform: {
                maxHeight: 1024,
                maxWidth: 1024, 
                preferredContentType: WEBP
              })
            }
          }
        }
        sellingPlanAllocation {
          sellingPlan {
            name
            deliveryPolicy {
              ... on SellingPlanRecurringDeliveryPolicy {
                intervalCount
                interval
              }
            }
          }
        }
      }
    }
  }
  cost {
    totalAmount {
      amount
      currencyCode
    }
    # The estimated amount, before taxes and discounts, for the customer to pay at checkout.
    subtotalAmount {
      amount
      currencyCode
    }
    # The estimated tax amount for the customer to pay at checkout.
    totalTaxAmount {
      amount
      currencyCode
    }
    # The estimated duty amount for the customer to pay at checkout.
    totalDutyAmount {
      amount
      currencyCode
    }
  }

  checkoutUrl
`;

export async function create(customerAccessToken: string, countryCode: string) {
  // TODO: add country / lang (also to other queries)

  const mutation = gql`
    mutation ($buyerIdentity: CartBuyerIdentityInput!) ${getContext()}{
      cartCreate(
        input: {
          buyerIdentity: $buyerIdentity
        }
      ) {
        cart {
          ${cartQuery}
        }
      }
    }
  `;

  let inputs = {}

  if (customerAccessToken) {
    inputs = {
      buyerIdentity: {
        customerAccessToken,
        countryCode
      }
    };
  } else {
    inputs = {
      buyerIdentity: {
        countryCode
      }
    };
  }


  const data: any = await graphQLClient?.request(mutation, inputs);

  return data?.cartCreate?.cart as Cart;
}

export async function load(cartId: string) {
  const query = gql`
    query ${getContext()}  {
      cart(
        id: "${cartId}"
      ) {
        ${cartQuery}
      }
    }
  `;

  const data: any = await graphQLClient?.request(query);

  return data?.cart as Cart;
}

export async function addItem(cartId: string, variantId: string, quantity: number, sellingPlanId: string | undefined = undefined) {
  const mutation = gql`
    mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) ${getContext()} {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        cart {
          ${cartQuery}
        }
      }
    }
  `;

  const variables = {
    cartId,
    lines: [
      {
        merchandiseId: variantId,
        sellingPlanId,
        quantity
      }
    ]
  };

  const data: any = await graphQLClient.request(mutation, variables);

  return data?.cartLinesAdd?.cart as Cart;
}

export async function updateQuantity(cartId: string, cartLineId: string, quantity: number) {
  const mutation = gql`
    mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) ${getContext()} {
      cartLinesUpdate(cartId: $cartId, lines: $lines) {
        cart {
          ${cartQuery}
        }
      }
    }
  `;

  const variables = {
    cartId,
    lines: [
      {
        id: cartLineId,
        quantity
      }
    ]
  };

  const data: any = await graphQLClient?.request(mutation, variables);

  return data?.cartLinesUpdate?.cart as Cart;
}

export async function removeItem(cartId: string, cartLineId: string) {
  const mutation = gql`
    mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) ${getContext()} {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
        cart {
          ${cartQuery}
        }
      }
    }
  `;

  const variables = {
    cartId,
    lineIds: [cartLineId]
  };

  const data: any = await graphQLClient?.request(mutation, variables);

  return data?.cartLinesRemove?.cart as Cart;
}

export async function updateBuyerIdentity(cartId: string, customerAccessToken: string | null, countryCode: string) {
  const mutation = gql`
    mutation cartBuyerIdentityUpdate($buyerIdentity: CartBuyerIdentityInput!, $cartId: ID!) {
      cartBuyerIdentityUpdate(buyerIdentity: $buyerIdentity, cartId: $cartId) {
        cart {
          ${cartQuery}
        }
      }
    }
  `;

  let variables = {}

  if (customerAccessToken) {
    variables = {
      cartId,
      buyerIdentity: {
        customerAccessToken,
        countryCode
      }
    };
  } else {
    variables = {
      cartId,
      buyerIdentity: {
        countryCode
      }
    };
  }

  const data: any = await graphQLClient?.request(mutation, variables);

  return data?.cartBuyerIdentityUpdate?.cart as Cart;
}

export async function applyDiscountCodes(cartId: string, discountCodes: string[]) {
  const query = gql`
    mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]) ${getContext()} {
      cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
        cart {
          ${cartQuery}
        }
      }
    }
  `;

  const variables = {
    cartId,
    discountCodes,
  };

  const data: any = await graphQLClient?.request(query, variables);

  return data?.cartDiscountCodesUpdate?.cart as Cart;
}